import { Pipe, PipeTransform } from '@angular/core';
import { AppConfigService } from '../appconfig/appconfig.service';

/**
 * Channel routing context for the by-channel file route.
 * Both fields are required to construct the URL; pass null/undefined to fall back
 * to a default placeholder (the upload pipe never gets called without a stored value).
 */
export interface FileUrlChannelContext {
  channelType: string;
  channelUniqueId: string;
}

/**
 * Resolves a stored bare file path (e.g. "botFiles/abc-123") to a fully qualified URL
 * the browser can load anonymously.
 *
 * Stored values in VisualsJson are environment-portable bare paths. The api host and the
 * tenant-resolving route get added here, at render time. Renderers always have a channel
 * context (channel edit page knows the channel; bot pages use the bot's signalr channel)
 * so the by-channel anonymous route on the api can resolve the tenant from the URL itself —
 * `<img>` tags don't carry Bearer tokens cross-origin, so JWT-based tenant resolution isn't
 * available for image rendering.
 *
 * Three input shapes are handled:
 *  - empty / null → empty string (caller can show a default placeholder)
 *  - data: URL → passed through (post-upload preview from FileReader)
 *  - bare "{container}/{fileId}" → "{api}/files/by-channel/{type}/{uniqueId}/{container}/{fileId}"
 *  - legacy absolute URL (already includes scheme + host + /files/...) → returned verbatim so
 *    pre-migration data keeps rendering until backfill scripts strip it
 *
 * Keep in sync with:
 *  - shared/Thoughtpost.ChatStyle.Infrastructure/Services/FileUrlResolver.cs (server DI)
 *  - channels/Thoughtpost.ChatStyle.Channels/FileUrlHelper.cs (channel-factory static)
 *  - Thoughtpost.ChatStyle.Elements/ClientApp/projects/crchat-chatwidget/src/lib/crchat-chatwidget.component.ts → resolveFileUrl
 */
@Pipe({ name: 'fileUrl', standalone: true })
export class FileUrlPipe implements PipeTransform {
  transform(value: string | null | undefined, context: FileUrlChannelContext | null | undefined): string {
    if (!value) return '';
    if (value.startsWith('data:')) return value;
    if (/^https?:\/\//i.test(value)) return value;

    if (!context || !context.channelType || !context.channelUniqueId) {
      // Without a channel context we can't authenticate the request cross-origin; the value
      // would 401 on the api anyway. Returning empty keeps the <img> from showing a broken
      // tile. Callers should always pass channel context for stored paths.
      return '';
    }

    const apiBase: string = (AppConfigService.settings.get('chatstyleapiServer') || '').trim();
    if (!apiBase) {
      // No api base configured. Building a relative "/files/by-channel/..." URL would just
      // hit the portal origin and 404. Return empty so callers fall back to a placeholder
      // (image-upload's previewSrc / `|| 'assets/...'` patterns) instead of rendering broken.
      return '';
    }
    const base = apiBase.endsWith('/') ? apiBase : apiBase + '/';
    const path = value.startsWith('/') ? value.substring(1) : value;
    return `${base}files/by-channel/${encodeURIComponent(context.channelType)}/${encodeURIComponent(context.channelUniqueId)}/${path}`;
  }
}
